// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  DollarSign,
  Package,
  User,
  MessageCircle,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  ChevronDown,
  Eye,
  FileImage
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

interface GraphicsOrder {
  id: string;
  athlete_id: string | null;
  vendor_id: string | null;
  package_id: string | null;
  total_amount: number;
  commission_amount: number;
  vendor_payout: number;
  status: string | null;
  payment_status: string | null;
  custom_requirements: string | null;
  delivery_date: string | null;
  graphics_urls: string[] | null;
  revision_count: number | null;
  athlete_feedback: string | null;
  vendor_notes: string | null;
  created_at: string;
  updated_at: string;
  package?: {
    name: string;
    package_type: string;
    price: number;
    description: string;
  };
  athlete?: {
    name: string;
    email: string;
  };
  message_count?: number;
  image_count?: number;
  has_unread_messages?: boolean;
}

const VendorOrders: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<GraphicsOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<GraphicsOrder | null>(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    totalRevenue: 0,
  });

  useEffect(() => {
    fetchOrders();

    // Set up real-time subscription for graphics_messages updates
    const messagesChannel = supabase
      .channel('graphics_messages_updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'graphics_messages'
        },
        (payload) => {
          console.log('📧 Real-time message update:', payload);
          // Refresh orders when messages are updated
          fetchOrders();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(messagesChannel);
    };
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);

      if (!user?.id) {
        console.error('No authenticated user found');
        toast.error('Please log in to view orders');
        return;
      }

      // First get the vendor profile
      const { data: vendorProfile, error: vendorError } = await supabase
        .from('graphics_vendor_profiles')
        .select('id')
        .eq('user_id', user?.id)
        .single();

      if (vendorError) {
        console.error('Vendor profile error:', vendorError);
        if (vendorError.code === 'PGRST116') {
          toast.error('No vendor profile found. Please set up your vendor profile first.');
        } else {
          toast.error('Failed to fetch vendor profile');
        }
        return;
      }

      // Then fetch orders for this vendor with message and image counts
      const { data, error } = await supabase
        .from('graphics_orders')
        .select(`
          *,
          package:graphics_packages!package_id(name, package_type, price, description),
          athlete:profiles!athlete_id(name, email)
        `)
        .eq('vendor_id', vendorProfile.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch message and image counts for each order
      const ordersWithCounts = await Promise.all(
        (data || []).map(async (order) => {
          console.log('🔍 Fetching messages for order:', order.id);
          
          // Get message count and check for unread messages
          const { data: messagesData, error: messagesError } = await supabase
            .from('graphics_messages')
            .select('id, sender_id, attachments, created_at')
            .eq('order_id', order.id);

          console.log('📧 Messages query result:', {
            orderId: order.id,
            messagesData,
            messagesError,
            vendorProfile: vendorProfile.id,
            orderVendorId: order.vendor_id
          });

          // Also test direct query to see if messages exist
          const { data: directMessages } = await supabase
            .from('graphics_messages')
            .select('*')
            .eq('order_id', order.id);
          
          console.log('🔍 Direct message query (bypassing RLS):', {
            orderId: order.id,
            directMessages,
            messageCount: directMessages?.length || 0
          });

          let messageCount = 0;
          let imageCount = 0;
          let hasUnreadMessages = false;

          if (!messagesError && messagesData) {
            messageCount = messagesData.length;
            
            // Count total images from all message attachments
            imageCount = messagesData.reduce((total, msg) => {
              return total + (msg.attachments?.length || 0);
            }, 0);

            // Check if there are messages from customer (not vendor) - compare with vendor profile ID
            const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
            
            // Get current user's profile ID to compare against sender_id
            const { data: currentUserProfile } = await supabase
              .from('profiles')
              .select('id')
              .eq('user_id', user?.id)
              .single();

            console.log('🔍 Current user profile:', currentUserProfile);
            console.log('🔍 Message sender IDs:', messagesData.map(m => m.sender_id));
            console.log('🔍 Vendor profile ID:', currentUserProfile?.id);

            hasUnreadMessages = messagesData.some(msg => {
              const isFromCustomer = msg.sender_id !== currentUserProfile?.id;
              const isRecent = msg.created_at > oneDayAgo;
              console.log(`📤 Message ${msg.id}: sender=${msg.sender_id}, isFromCustomer=${isFromCustomer}, isRecent=${isRecent}`);
              return isFromCustomer && isRecent;
            });

            console.log('📊 Message stats for order:', {
              orderId: order.id,
              messageCount,
              imageCount,
              hasUnreadMessages,
              vendorProfileId: currentUserProfile?.id
            });
          } else if (messagesError) {
            console.error('❌ Error fetching messages:', messagesError);
          }

          return {
            ...order,
            message_count: messageCount,
            image_count: imageCount,
            has_unread_messages: hasUnreadMessages,
          };
        })
      );

      setOrders(ordersWithCounts);

      // Calculate stats
      const total = ordersWithCounts?.length || 0;
      const pending = ordersWithCounts?.filter(order => order.status === 'pending').length || 0;
      const completed = ordersWithCounts?.filter(order => order.status === 'completed').length || 0;
      const totalRevenue = ordersWithCounts?.reduce((sum, order) => sum + (order.total_amount || 0), 0) || 0;

      setStats({
        total,
        pending,
        completed,
        totalRevenue,
      });

    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('graphics_orders')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString(),
        })
        .eq('id', orderId);

      if (error) throw error;

      toast.success('Order status updated successfully');
      fetchOrders(); // Refresh the orders
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Failed to update order status');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { variant: 'secondary' as const, icon: Clock, color: 'text-yellow-600' },
      confirmed: { variant: 'default' as const, icon: CheckCircle, color: 'text-blue-600' },
      in_progress: { variant: 'default' as const, icon: Package, color: 'text-purple-600' },
      completed: { variant: 'default' as const, icon: CheckCircle, color: 'text-green-600' },
      cancelled: { variant: 'destructive' as const, icon: AlertCircle, color: 'text-red-600' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </Badge>
    );
  };

  const formatPrice = (amount: number) => {
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin mr-2" />
        <span>Loading orders...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Orders</h1>
        <p className="text-muted-foreground">
          Manage your graphics design orders and track your revenue.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">
              All time orders
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Orders</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pending}</div>
            <p className="text-xs text-muted-foreground">
              Awaiting action
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.completed}</div>
            <p className="text-xs text-muted-foreground">
              Successfully delivered
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPrice(stats.totalRevenue)}</div>
            <p className="text-xs text-muted-foreground">
              All time earnings
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Orders Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Orders</CardTitle>
          <CardDescription>
            A list of your graphics design orders and their current status.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {orders.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Package</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Messages</TableHead>
                  <TableHead>Images</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                 {orders.map((order) => (
                    <TableRow 
                      key={order.id} 
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => navigate(`/vendor-graphics-dashboard/orders/${order.id}`)}
                    >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{order.athlete?.name || 'Unknown Athlete'}</div>
                          <div className="text-sm text-muted-foreground">
                            {order.athlete?.email || 'No email'}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{order.custom_requirements || 'Custom Graphics Order'}</div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium capitalize">{order.package?.name || order.package?.package_type || 'Custom Package'}</div>
                        <div className="text-sm text-muted-foreground">
                          {order.package?.description && order.package.description.substring(0, 50)}
                          {order.package?.description && order.package.description.length > 50 && '...'}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatPrice(order.total_amount)}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(order.status)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <MessageCircle className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{order.message_count || 0}</span>
                        {order.has_unread_messages && (
                          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" title="New messages" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <FileImage className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{order.image_count || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(order.created_at)}
                    </TableCell>
                     <TableCell className="text-right">
                       <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                         <DropdownMenu>
                           <DropdownMenuTrigger asChild>
                             <Button
                               variant="outline"
                               size="sm"
                               onClick={(e) => e.stopPropagation()}
                             >
                               Update Status
                               <ChevronDown className="h-4 w-4 ml-1" />
                             </Button>
                           </DropdownMenuTrigger>
                           <DropdownMenuContent align="end" className="bg-background border shadow-md z-50">
                             <DropdownMenuItem
                               onClick={(e) => {
                                 e.stopPropagation();
                                 updateOrderStatus(order.id, 'pending');
                               }}
                               disabled={order.status === 'pending'}
                             >
                               Pending
                             </DropdownMenuItem>
                             <DropdownMenuItem
                               onClick={(e) => {
                                 e.stopPropagation();
                                 updateOrderStatus(order.id, 'confirmed');
                               }}
                               disabled={order.status === 'confirmed'}
                             >
                               Confirmed
                             </DropdownMenuItem>
                             <DropdownMenuItem
                               onClick={(e) => {
                                 e.stopPropagation();
                                 updateOrderStatus(order.id, 'in_progress');
                               }}
                               disabled={order.status === 'in_progress'}
                             >
                               In Progress
                             </DropdownMenuItem>
                             <DropdownMenuItem
                               onClick={(e) => {
                                 e.stopPropagation();
                                 updateOrderStatus(order.id, 'completed');
                               }}
                               disabled={order.status === 'completed'}
                             >
                               Completed
                             </DropdownMenuItem>
                             <DropdownMenuItem
                               onClick={(e) => {
                                 e.stopPropagation();
                                 updateOrderStatus(order.id, 'cancelled');
                               }}
                               disabled={order.status === 'cancelled'}
                               className="text-destructive"
                             >
                               Cancelled
                             </DropdownMenuItem>
                           </DropdownMenuContent>
                         </DropdownMenu>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/vendor-graphics/orders/${order.id}`);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                       </div>
                     </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Orders Yet</h3>
              <p className="text-muted-foreground mb-4">
                When customers place orders for your graphics services, they'll appear here.
              </p>
              <p className="text-sm text-muted-foreground">
                Make sure your packages are active and properly configured.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VendorOrders;